/**
 * Dynamic Router Module - ZERO CODE PUSH ROUTING
 *
 * Architecture:
 * 1. Routes defined in routeTabConfig.js with componentPath field
 * 2. Components loaded dynamically via React.lazy() - NO static imports needed
 * 3. rbacEnabled: true  -> Route controlled by RBAC (respects allowed flag)
 * 4. rbacEnabled: false -> Route NOT controlled by RBAC (renders if componentPath exists)
 * 5. props: {} -> Optional props passed to the rendered component
 * 6. featureFlag: "FLAG_NAME" -> Optional feature flag gating (checked at render time)
 *
 * To add a new route (ZERO code changes to this file):
 * 1. Add entry in routeTabConfig.js with componentPath (e.g., "pages/MyPage")
 * 2. Create component file at that path (e.g., src/pages/MyPage.jsx)
 * That's it! No imports, no maps, no code changes here.
 */

import React, { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import ProtectedRoute from 'components/ProtectedRoute';
import RouteDebugPage from 'components/RouteDebugPage';
import { ROUTES_TAB_CONFIG, convertConfigToPermissions } from 'config/routeTabConfig';

// Feature flag imports - lazy checked at route render time
import {
  OTS_DELHIVERY_ACCESS,
  KPI_DASHBOARD_ACCESS,
  CONVIN_DASHBOARD_ACCESS,
  AUTO_QA_DASHBOARD_ACCESS,
  MTD_REPORT_ACCESS,
  COMMUNITY_ACCESS_EMAILS,
} from 'modules/utilities/utility';
import secureLocalStorage from 'react-secure-storage';

/**
 * Check if a feature flag allows access.
 * Returns true if no flag is set, or if the flag evaluates to truthy.
 */
const checkFeatureFlag = (flagName) => {
  if (!flagName) return true;
  const flagMap = {
    OTS_DELHIVERY_ACCESS,
    KPI_DASHBOARD_ACCESS,
    CONVIN_DASHBOARD_ACCESS,
    AUTO_QA_DASHBOARD_ACCESS,
    MTD_REPORT_ACCESS,
  };
  // Special case: COMMUNITY_ACCESS_EMAILS is an array, check email membership
  if (flagName === 'COMMUNITY_ACCESS_EMAILS') {
    return COMMUNITY_ACCESS_EMAILS.includes(secureLocalStorage.getItem("email"));
  }
  // Boolean flags
  if (flagName in flagMap) return !!flagMap[flagName];
  // Unknown flag — allow by default
  return true;
};

// Cache the permissions for path map building
let _cachedPermissions = null;
const getCachedPermissions = () => {
  if (!_cachedPermissions) _cachedPermissions = convertConfigToPermissions();
  return _cachedPermissions;
};

/**
 * Build path -> { componentPath, props, featureFlag } lookup from ROUTES_TAB_CONFIG.
 * All keys stored in lowercase for case-insensitive matching.
 */
const buildComponentPathMap = () => {
  const permissions = getCachedPermissions();
  const map = {};
  Object.values(permissions).forEach(group => {
    Object.values(group.routes || {}).forEach(route => {
      if (route.componentPath) {
        map[(route.path || '').toLowerCase()] = {
          componentPath: route.componentPath,
          props: route.props || {},
          featureFlag: route.featureFlag || null,
        };
      }
      Object.values(route.subRoutes || {}).forEach(sub => {
        if (sub.componentPath) {
          map[(sub.path || '').toLowerCase()] = {
            componentPath: sub.componentPath,
            props: sub.props || {},
            featureFlag: sub.featureFlag || null,
          };
        }
      });
    });
  });
  return map;
};

const COMPONENT_PATH_MAP = buildComponentPathMap();

/** Cache for React.lazy components to prevent re-creation on every render */
const lazyCache = {};

/**
 * Check if a componentPath exists for a given route path (no side effects).
 */
const hasComponentPath = (routePath) => {
  return !!COMPONENT_PATH_MAP[(routePath || '').toLowerCase()];
};

/**
 * Get or create a React.lazy component for a route path.
 * Returns a React component type, or null if no componentPath mapping exists.
 * @param {string} routePath - The route path to resolve
 * @param {string} [overrideComponentPath] - Optional componentPath from permissions (takes priority over config)
 */
const getDynamicComponent = (routePath, overrideComponentPath) => {
  const normalizedPath = (routePath || '').toLowerCase();

  // If override provided, use a cache key that includes it
  const cacheKey = overrideComponentPath ? `${normalizedPath}::${overrideComponentPath}` : normalizedPath;

  if (lazyCache[cacheKey]) {
    return lazyCache[cacheKey];
  }

  const mapEntry = COMPONENT_PATH_MAP[normalizedPath];
  const componentPath = overrideComponentPath || (mapEntry ? mapEntry.componentPath : null);
  if (!componentPath) {
    return null;
  }

  const parts = componentPath.split('/');
  const dir = parts[0]; // 'pages' or 'modules'
  const subPath = parts.slice(1).join('/');

  let LazyComponent = null;

  if (dir === 'pages') {
    LazyComponent = React.lazy(() =>
      import(`../pages/${subPath}`)
        .catch(err => {
          return {
            default: () => (
              <RouteDebugPage
                type="component-missing"
                path={routePath}
                routeName={routePath}
                debugInfo={{ componentPath, error: `Failed to load: src/pages/${subPath}.jsx - ${err.message}` }}
              />
            )
          };
        })
    );
  } else if (dir === 'modules') {
    LazyComponent = React.lazy(() =>
      import(`../modules/${subPath}`)
        .catch(err => {
          return {
            default: () => (
              <RouteDebugPage
                type="component-missing"
                path={routePath}
                routeName={routePath}
                debugInfo={{ componentPath, error: `Failed to load: src/modules/${subPath}.jsx - ${err.message}` }}
              />
            )
          };
        })
    );
  } else {
    return null;
  }

  if (LazyComponent) {
    lazyCache[cacheKey] = LazyComponent;
  }

  return LazyComponent;
};

/**
 * Get props for a route from the path map.
 */
const getRouteProps = (routePath) => {
  const mapEntry = COMPONENT_PATH_MAP[(routePath || '').toLowerCase()];
  return mapEntry ? mapEntry.props || {} : {};
};

/**
 * Get featureFlag for a route from the path map.
 */
const getRouteFeatureFlag = (routePath) => {
  const mapEntry = COMPONENT_PATH_MAP[(routePath || '').toLowerCase()];
  return mapEntry ? mapEntry.featureFlag || null : null;
};

/** Loading fallback for Suspense */
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <Spin size="large" />
  </div>
);

/** Wrap a lazy component in Suspense, with optional props */
const wrapLazy = (Component, props = {}) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component {...props} />
  </Suspense>
);

/**
 * Generate routes based on user's RBAC permissions.
 * Components are loaded dynamically via componentPath in routeTabConfig.
 */
export const generateDynamicRoutes = (permissions = {}) => {
  const routes = [];

  if (!permissions || Object.keys(permissions).length === 0) {
    return routes;
  }

  Object.entries(permissions).forEach(([groupName, groupData]) => {
    if (!groupData.routes) {
      return;
    }

    Object.entries(groupData.routes).forEach(([routeKey, routeData]) => {
      // Handle routes with subroutes FIRST — regardless of parent's rbacEnabled
      // Each subroute is independently controlled by its own allowed/rbacEnabled flags
      if (routeData.subRoutes && Object.keys(routeData.subRoutes).length > 0) {
        // Auto-redirect parent path to first accessible subroute
        const subEntries = Object.values(routeData.subRoutes);
        const accessible = subEntries.filter(sub => {
          if (!checkFeatureFlag(sub.featureFlag || getRouteFeatureFlag(sub.path))) return false;
          if (sub.rbacEnabled === false) return true;
          return !!sub.allowed;
        });
        if (accessible.length > 0) {
          const first = accessible.reduce((min, cur) =>
            (cur.order ?? Infinity) < (min.order ?? Infinity) ? cur : min
          );
          const parentPath = (routeData.path || '').toLowerCase();
          routes.push(
            <Route key={`${parentPath}--redirect`} path={parentPath}
              element={<Navigate to={(first.path || '').toLowerCase()} replace />} />
          );
        }

        Object.entries(routeData.subRoutes).forEach(([subRouteKey, subRouteData]) => {
          // Check feature flag
          const featureFlag = subRouteData.featureFlag || getRouteFeatureFlag(subRouteData.path);
          if (!checkFeatureFlag(featureFlag)) return;

          // rbacEnabled: false subroute -> render if componentPath exists
          if (subRouteData.rbacEnabled === false) {
            const Component = getDynamicComponent(subRouteData.path, subRouteData.componentPath);
            if (!Component) {
              return;
            }
            const normalizedPath = (subRouteData.path || '').toLowerCase();
            const props = subRouteData.props || getRouteProps(subRouteData.path);
            routes.push(
              <Route key={normalizedPath} path={normalizedPath} element={
                <ProtectedRoute path={normalizedPath} element={wrapLazy(Component, props)} routeData={subRouteData} requiredPermission={`${groupName}.${routeKey}.${subRouteKey}`} />
              } />
            );
            return;
          }

          // rbacEnabled: true subroute -> respect allowed flag
          // Hidden groups (SharedRoutes, etc.) → always allow
          if (!subRouteData.allowed && !groupData.hidden) {
            return;
          }

          const normalizedPath = (subRouteData.path || '').toLowerCase();
          const props = subRouteData.props || getRouteProps(subRouteData.path);
          const Component = getDynamicComponent(subRouteData.path, subRouteData.componentPath);
          const element = Component ? wrapLazy(Component, props) : (
            <RouteDebugPage
              type="component-missing"
              path={subRouteData.path}
              routeName={subRouteData.name}
              debugInfo={{
                hasComponentPath: hasComponentPath(subRouteData.path),
                rbacEnabled: subRouteData.rbacEnabled,
                allowed: subRouteData.allowed,
                permissionGroup: groupName,
                hint: 'Add componentPath to this route in routeTabConfig.js',
              }}
            />
          );

          // Hidden groups → force allowed:true so ProtectedRoute doesn't block
          const effectiveSubRouteData = groupData.hidden
            ? { ...subRouteData, allowed: true }
            : subRouteData;

          routes.push(
            <Route key={normalizedPath} path={normalizedPath} element={
              <ProtectedRoute path={normalizedPath} element={element} routeData={effectiveSubRouteData} requiredPermission={`${groupName}.${routeKey}.${subRouteKey}`} />
            } />
          );
        });
        return;
      }

      // No subroutes — handle as leaf route
      // Hidden groups (SharedRoutes, etc.) → always allow (utility routes needed by all users)
      // rbacEnabled: true -> respect allowed flag; rbacEnabled: false -> always allow
      if (routeData.rbacEnabled !== false && !routeData.allowed && !groupData.hidden) {
        return;
      }

      // Check feature flag
      const featureFlag = routeData.featureFlag || getRouteFeatureFlag(routeData.path);
      if (!checkFeatureFlag(featureFlag)) return;

      // Handle routes without subroutes
      const normalizedPath = (routeData.path || '').toLowerCase();
      const props = routeData.props || getRouteProps(routeData.path);
      const Component = getDynamicComponent(routeData.path, routeData.componentPath);
      const element = Component ? wrapLazy(Component, props) : (
        <RouteDebugPage
          type="component-missing"
          path={routeData.path}
          routeName={routeData.name}
          debugInfo={{
            hasComponentPath: hasComponentPath(routeData.path),
            rbacEnabled: routeData.rbacEnabled,
            allowed: routeData.allowed,
            permissionGroup: groupName,
            hint: 'Add componentPath to this route in routeTabConfig.js',
          }}
        />
      );

      // Hidden groups → force allowed:true so ProtectedRoute doesn't block
      const effectiveRouteData = groupData.hidden
        ? { ...routeData, allowed: true }
        : routeData;

      routes.push(
        <Route key={normalizedPath} path={normalizedPath} element={
          <ProtectedRoute path={normalizedPath} element={element} routeData={effectiveRouteData} requiredPermission={`${groupName}.${routeKey}`} />
        } />
      );
    });
  });

  return routes;
};

/**
 * Get allowed routes for current user
 */
export const getAllowedRoutes = (permissions = {}) => {
  const allowedRoutes = [];

  Object.entries(permissions).forEach(([groupName, groupData]) => {
    if (!groupData.routes) return;

    Object.entries(groupData.routes).forEach(([routeKey, routeData]) => {
      if (routeData.rbacEnabled === false) {
        if (hasComponentPath(routeData.path)) {
          allowedRoutes.push({
            path: (routeData.path || '').toLowerCase(),
            name: routeData.name,
            groupName,
            routeKey
          });
        }
        return;
      }
      if (routeData.allowed) {
        allowedRoutes.push({
          path: (routeData.path || '').toLowerCase(),
          name: routeData.name,
          groupName,
          routeKey
        });
      }
    });
  });

  return allowedRoutes;
};

/**
 * Check if user has permission for a specific route
 */
export const hasRoutePermission = (routePath, permissions = {}) => {
  const normalizedInput = (routePath || '').toLowerCase();
  for (const groupData of Object.values(permissions)) {
    if (!groupData.routes) continue;
    for (const routeData of Object.values(groupData.routes)) {
      const normalizedPath = (routeData.path || '').toLowerCase();
      if (normalizedPath !== normalizedInput) continue;
      if (routeData.rbacEnabled === false) return hasComponentPath(routeData.path);
      if (routeData.allowed) return true;
    }
  }
  return false;
};

/**
 * Get route metadata by path
 */
export const getRouteMetadata = (routePath, permissions = {}) => {
  const normalizedInput = (routePath || '').toLowerCase();
  for (const [groupName, groupData] of Object.entries(permissions)) {
    if (!groupData.routes) continue;
    for (const routeData of Object.values(groupData.routes)) {
      if ((routeData.path || '').toLowerCase() === normalizedInput) {
        return { ...routeData, permissionGroup: groupName };
      }
    }
  }
  return null;
};

export { COMPONENT_PATH_MAP };

export default {
  generateDynamicRoutes,
  getAllowedRoutes,
  hasRoutePermission,
  getRouteMetadata,
};
